import { ForecastResponse } from "@/api/kefc";
import {
  CityKey,
  Pick,
  SERVICES,
  TARIFF_LABEL,
  pickCode,
  pickId,
  shade,
} from "@/features/services";

export const HOURS = 24;

export interface Series {
  id: string;
  pick: Pick;
  code: number;
  /** Подпись в тултипе и спросе: «Еда», а при двух тарифах одного сервиса — «Еда Авто». */
  label: string;
  short: string;
  tnote: string;
  color: string;
  logo: string;
  /** По этому тарифу в городе данных нет. */
  dead: boolean;
  v: (number | null)[];
  src: ("live" | "fore" | null)[];
}

export interface Board {
  /** Эпоха-час первого индекса окна. */
  startEpoch: number;
  /** Смещение города от UTC: все подписи часов — в его времени, не в времени устройства. */
  tz: number;
  /** Локальный час (0–23) для каждого индекса окна. */
  hours: number[];
  /** Индекс «сейчас» — последнего часа, по которому есть факт. */
  nowIndex: number;
  /** «Актуально на HH:MM» — час снимка в местном времени. */
  madeLocal: string;
  series: Series[];
  hasData: boolean;
}

const HOUR_MS = 3600e3;
const epochHour = (iso: string) => Math.floor(Date.parse(iso) / HOUR_MS);
const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Прогноз kef-compare → 24-часовое окно для графика.
 *
 * Снимок покрывает часы от «сейчас − 6» до «сейчас + 24». Окно начинается с
 * первого часа снимка, чтобы график всегда был заполнен целиком, а не обрывался
 * на границе суток; подписи оси считаются от него в часовом поясе ГОРОДА.
 */
export function buildBoard(
  forecast: ForecastResponse,
  city: CityKey,
  picks: Pick[]
): Board {
  const cityData = forecast.cities[city];
  const tz = cityData?.tz ?? 3;

  const allHours = (cityData?.tariffs ?? []).flatMap((t) =>
    t.hours.map((h) => epochHour(h.h))
  );
  const nowEpoch = Math.floor(Date.now() / HOUR_MS);
  const start = allHours.length ? Math.min(...allHours) : nowEpoch - 6;

  const hours = Array.from({ length: HOURS }, (_, i) => (((start + i + tz) % 24) + 24) % 24);

  const madeEpoch = forecast.made_hour ? epochHour(forecast.made_hour) : nowEpoch;
  const madeLocal = `${pad((((madeEpoch + tz) % 24) + 24) % 24)}:00`;

  const dupCount: Partial<Record<string, number>> = {};
  const seen: Partial<Record<string, number>> = {};
  picks.forEach((p) => {
    dupCount[p.k] = (dupCount[p.k] ?? 0) + 1;
  });

  const series: Series[] = picks.map((p) => {
    const svc = SERVICES[p.k];
    const code = pickCode(p);
    const dup = seen[p.k] ?? 0;
    seen[p.k] = dup + 1;
    const tariff = cityData?.tariffs.find((t) => t.tariff === code);
    const v: (number | null)[] = Array(HOURS).fill(null);
    const src: ("live" | "fore" | null)[] = Array(HOURS).fill(null);
    tariff?.hours.forEach((h) => {
      const i = epochHour(h.h) - start;
      if (i >= 0 && i < HOURS) {
        v[i] = Math.round(h.v);
        src[i] = h.src;
      }
    });
    return {
      id: pickId(p),
      pick: p,
      code,
      label: (dupCount[p.k] ?? 0) > 1 ? `${svc.short} ${TARIFF_LABEL[p.t]}` : svc.short,
      short: svc.short,
      tnote: TARIFF_LABEL[p.t],
      color: dup ? shade(svc.color, 52 * dup) : svc.color,
      logo: svc.logo,
      dead: !tariff,
      v,
      src,
    };
  });

  /*
   * Граница факта и прогноза — одна на весь график, как в макете, и берётся
   * из данных: последний час, по которому хоть у одного выбранного тарифа есть
   * настоящие чек и кэф (`src: live`).
   *
   * Почему одна, а не своя у каждой линии. Полнота факта у тарифов разная:
   * на 08.09 у Пешего и Авто он до 10:00, а у Такси — один час из двадцати
   * двух, дальше сервис подставляет типовые значения. С отдельными границами
   * пунктир у Такси начинался за три часа до маркера «сейчас», и график сам
   * себе противоречил. Часы устройства не участвуют: и подписи, и граница
   * живут во времени города (`tz` снимка), клиентские часы — только запасной
   * путь, если факта в снимке нет вовсе.
   */
  const liveIndexes = series.flatMap((s) =>
    s.src.map((v, i) => (v === "live" ? i : -1)).filter((i) => i >= 0)
  );
  const nowIndex = liveIndexes.length
    ? Math.max(...liveIndexes)
    : Math.max(0, Math.min(HOURS - 1, madeEpoch - start));

  return {
    startEpoch: start,
    tz,
    hours,
    nowIndex,
    madeLocal,
    series,
    hasData: series.some((s) => !s.dead),
  };
}

/** Лидер и второе место на выбранном часе. */
export function leadersAt(board: Board, hour: number) {
  const ranked = board.series
    .filter((s) => !s.dead && s.v[hour] != null)
    .sort((a, b) => (b.v[hour] as number) - (a.v[hour] as number));
  return { best: ranked[0] ?? null, second: ranked[1] ?? null };
}

export const fmtHour = (h: number) => `${pad(h)}:00`;
